import { ReportLocationEnum } from '../enums/reportLocationEnum';
import { RotationTreeEnum } from '../enums/rotationTreeEnum';
import { BerthCall } from '../types/ViewModel/BerthCall';
import { CargoOperation } from '../types/ViewModel/CargoOperation';
import { IRotationNode } from '../types/ViewModel/IRotationNode';
import { PortCall } from '../types/ViewModel/PortCall';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';

export default () => {
    const { triggerQueryParamsUpdate, getQueryParamValueFromUrl } = useQueryParamsHandler();

    const setAppAccessToken = (query: URLSearchParams, params: URLSearchParams) => {
        const appAccessToken = query.get('appAccessToken');
        if (appAccessToken) {
            params.set('appAccessToken', appAccessToken);
        }
    };

    const getQueryParam = (name: string): string | null => {
        const query = new URLSearchParams(window.location.search);
        return query.get(name);
    };

    const resetQueryParamPortCall = () => {
        const imoNr = getQueryParamValueFromUrl('imoNumber');

        if (imoNr) {
            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');

            const queryParamValues: QueryParamValueProps[] = [];
            queryParamValues.push({ name: 'imoNumber', newValue: imoNr });
            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }
            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamCargoTempLog = (date: string) => {
        const imoNumber = getQueryParamValueFromUrl('imoNumber');

        if (imoNumber) {
            const queryParamValues: QueryParamValueProps[] = [];
            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');

            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }
            queryParamValues.push({ name: 'imoNumber', newValue: imoNumber });
            queryParamValues.push({ name: 'aolDate', newValue: date });
            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamChemicalOrder = (chemicalOrderId: string) => {
        const imoNumber = getQueryParamValueFromUrl('imoNumber');
        const portCallId = getQueryParamValueFromUrl('portCallId');
        if (imoNumber && portCallId && chemicalOrderId) {
            const queryParamValues: QueryParamValueProps[] = [];

            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');

            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            queryParamValues.push({ name: 'imoNumber', newValue: imoNumber });
            queryParamValues.push({ name: 'portCallId', newValue: portCallId });
            queryParamValues.push({ name: 'reportType', newValue: 'order' });
            queryParamValues.push({ name: 'chemicalOrderId', newValue: chemicalOrderId });
            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamChemicalReportType = (reportType: string) => {
        const imoNumber = getQueryParamValueFromUrl('imoNumber');
        const portCallId = getQueryParamValueFromUrl('portCallId');
        const chemicalOrderId = getQueryParamValueFromUrl('chemicalOrderId');
        if (imoNumber && portCallId && reportType) {
            const queryParamValues: QueryParamValueProps[] = [];

            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');

            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            queryParamValues.push({ name: 'imoNumber', newValue: imoNumber });
            queryParamValues.push({ name: 'portCallId', newValue: portCallId });
            queryParamValues.push({ name: 'reportType', newValue: reportType.toString() });
            if (chemicalOrderId && reportType === 'order') {
                queryParamValues.push({ name: 'chemicalOrderId', newValue: chemicalOrderId });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamChemicalPortCall = (portCallId: string) => {
        const imoNumber = getQueryParamValueFromUrl('imoNumber');
        const reportType = getQueryParamValueFromUrl('reportType');
        if (imoNumber) {
            const queryParamValues: QueryParamValueProps[] = [];

            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');
            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            queryParamValues.push({ name: 'imoNumber', newValue: imoNumber });
            queryParamValues.push({ name: 'portCallId', newValue: portCallId });
            if (reportType) {
                queryParamValues.push({ name: 'reportType', newValue: reportType.toString() });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamPortCall = (value: string, reportLocation: ReportLocationEnum) => {
        const imoNr = getQueryParamValueFromUrl('imoNumber');

        if (imoNr) {
            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');

            const queryParamValues: QueryParamValueProps[] = [];
            queryParamValues.push({ name: 'imoNumber', newValue: imoNr });
            queryParamValues.push({ name: 'portCallId', newValue: value });
            queryParamValues.push({ name: 'reportLocation', newValue: reportLocation.toString() });
            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamBerthCall = (node: IRotationNode) => {
        const imoNr = getQueryParamValueFromUrl('imoNumber');

        if (imoNr) {
            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');
            const berthCallId = (node as BerthCall).berthCallId;
            const portCallId = node.portCall?.id;
            const reportLocation = node.portCall?.reportLocation;
            const queryParamValues: QueryParamValueProps[] = [];
            queryParamValues.push({ name: 'imoNumber', newValue: imoNr });
            // TODO: NULL-SAFETY - Potential null reference: portCallId and reportLocation could be undefined if node.portCall is null/undefined, but toString() is called without null checks
            queryParamValues.push({ name: 'portCallId', newValue: portCallId.toString() });
            queryParamValues.push({ name: 'berthCallId', newValue: berthCallId.toString() });
            queryParamValues.push({ name: 'reportLocation', newValue: reportLocation.toString() });
            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamCargoOperation = (node: IRotationNode) => {
        const imoNr = getQueryParamValueFromUrl('imoNumber');

        if (imoNr) {
            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');

            const cargoOperation = node as CargoOperation;
            const portCallId = cargoOperation.berthCall?.portCall?.id;
            const reportLocation = cargoOperation.berthCall?.portCall?.reportLocation;
            const berthCallId = cargoOperation.berthCall?.berthCallId;
            const cargoOperationId = cargoOperation.cargoOperationId;

            const queryParamValues: QueryParamValueProps[] = [];

            if (portCallId && reportLocation && berthCallId && cargoOperationId) {
                queryParamValues.push({ name: 'imoNumber', newValue: imoNr });
                queryParamValues.push({ name: 'portCallId', newValue: portCallId.toString() });
                queryParamValues.push({ name: 'berthCallId', newValue: berthCallId.toString() });
                queryParamValues.push({ name: 'reportLocation', newValue: reportLocation.toString() });
                queryParamValues.push({ name: 'cargoOperationId', newValue: cargoOperationId.toString() });
            }

            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamEvent = (value: string) => {
        const imoNumber = getQueryParamValueFromUrl('imoNumber');
        const queryParamValues: QueryParamValueProps[] = [];
        if (imoNumber) {
            queryParamValues.push({ name: 'imoNumber', newValue: imoNumber.toString() });

            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');
            const portCallId = getQueryParamValueFromUrl('portCallId');

            if (portCallId) {
                const reportLocation = getQueryParamValueFromUrl('reportLocation');
                if (reportLocation) {
                    queryParamValues.push({ name: 'portCallId', newValue: portCallId.toString() });
                    queryParamValues.push({ name: 'reportLocation', newValue: reportLocation.toString() });

                    const berthCallId = getQueryParamValueFromUrl('berthCallId');
                    if (berthCallId) {
                        queryParamValues.push({ name: 'berthCallId', newValue: berthCallId.toString() });

                        const cargoOperationId = getQueryParamValueFromUrl('cargoOperationId');
                        if (cargoOperationId) {
                            queryParamValues.push({ name: 'cargoOperationId', newValue: cargoOperationId.toString() });
                        }
                    }
                }
                queryParamValues.push({ name: 'eventId', newValue: value });
            }

            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const setQueryParamToolbox = (eventTypeId: number, seqNr: number | null) => {
        const queryParamValues: QueryParamValueProps[] = [];
        const imoNumber = getQueryParamValueFromUrl('imoNumber');

        if (imoNumber) {
            queryParamValues.push({ name: 'imoNumber', newValue: imoNumber });

            const appAccessToken = getQueryParamValueFromUrl('appAccessToken');
            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            const portCallId = getQueryParamValueFromUrl('portCallId');

            if (portCallId) {
                const reportLocation = getQueryParamValueFromUrl('reportLocation');
                if (reportLocation) {
                    queryParamValues.push({ name: 'portCallId', newValue: portCallId.toString() });
                    queryParamValues.push({ name: 'reportLocation', newValue: reportLocation.toString() });

                    const berthCallId = getQueryParamValueFromUrl('berthCallId');
                    if (berthCallId) {
                        queryParamValues.push({ name: 'berthCallId', newValue: berthCallId.toString() });

                        const cargoOperationId = getQueryParamValueFromUrl('cargoOperationId');
                        if (cargoOperationId) {
                            queryParamValues.push({ name: 'cargoOperationId', newValue: cargoOperationId.toString() });
                        }
                    }
                }

                queryParamValues.push({ name: 'eventTypeId', newValue: eventTypeId.toString() });
                if (seqNr) {
                    queryParamValues.push({ name: 'seqNr', newValue: seqNr.toString() });
                }
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const createPortCallListQueryParam = (rotation): string => {
        const maxPortCalls = 100;

        let qryParam = '';
        if (rotation && rotation.portCallArray && rotation.portCallArray.length > 0) {
            let pcIdList = rotation.portCallArray.filter((o) => o.reportLocation === ReportLocationEnum.Port).map((o) => o.portCallId);
            if (pcIdList.length > maxPortCalls) {
                pcIdList = pcIdList.splice(pcIdList.length - maxPortCalls, maxPortCalls);
            }
            pcIdList.forEach((pcId) => {
                qryParam += pcId.toString() + ',';
            });
            qryParam = qryParam.substr(0, qryParam.length - 1);
        }
        return qryParam;
    };

    const compareVoyNr = (a: number, b: number, desc: boolean): number => {
        let cmp = 0;
        if (a > b) {
            cmp = 1;
        } else if (b > a) {
            cmp = -1;
        }
        return desc ? cmp * -1 : cmp;
    };

    const createVoyageNrListQueryParam = (rotation): string => {
        let qryParam = '';
        if (rotation && rotation.portCallArray && rotation.portCallArray.length > 0) {
            const voyNrList1: number[] = rotation.portCallArray
                .filter((o: PortCall) => o.reportLocation === ReportLocationEnum.Port)
                .map((o: PortCall) => o.voyageNumber1);
            const voyNrList2: number[] = rotation.portCallArray
                .filter((o) => o.reportLocation === ReportLocationEnum.Port && o.voyageNumber2)
                .map((o) => o.voyageNumber2);
            const voyNrList = [...voyNrList1, ...voyNrList2];
            const voyNrListfiltered = voyNrList.filter(function (v, i) {
                return voyNrList.indexOf(v) == i;
            });
            const voyNrListSorted = voyNrListfiltered.sort((a, b) => compareVoyNr(a, b, true));
            voyNrListSorted.forEach((voyNr) => {
                qryParam += voyNr.toString() + ',';
            });
            qryParam = qryParam.substring(0, qryParam.length - 1);
        }
        return qryParam;
    };

    const setQueryFromSelectedNode = (selectedNode: IRotationNode | null) => {
        const queryParamValues: QueryParamValueProps[] = [];

        const imoNr = getQueryParamValueFromUrl('imoNumber');
        const appAccessToken = getQueryParamValueFromUrl('appAccessToken');
        let portCallId: string | undefined = undefined;
        let reportLocation: string | undefined = undefined;
        let berthCallId: string | undefined = undefined;
        let cargoOperationId: string | undefined = undefined;

        if (imoNr) {
            if (selectedNode) {
                if (selectedNode.type === RotationTreeEnum.PortCall) {
                    portCallId = selectedNode.portCall?.portCallId?.toString();
                    reportLocation = selectedNode.reportLocation.toString();
                } else if (selectedNode.type === RotationTreeEnum.Sea) {
                    portCallId = selectedNode.portCall?.portCallId?.toString();
                    reportLocation = selectedNode.reportLocation.toString();
                } else if (selectedNode.type === RotationTreeEnum.BerthCall) {
                    portCallId = selectedNode.portCall?.portCallId?.toString();
                    reportLocation = selectedNode.reportLocation.toString();
                    berthCallId = (selectedNode as BerthCall).berthCallId?.toString();
                } else if (selectedNode.type === RotationTreeEnum.CargoOperation) {
                    portCallId = selectedNode.portCall?.portCallId?.toString();
                    reportLocation = selectedNode.reportLocation.toString();
                    berthCallId = (selectedNode as CargoOperation).berthCall?.berthCallId?.toString();
                    cargoOperationId = (selectedNode as CargoOperation).cargoOperationId?.toString();
                }
            }

            queryParamValues.push({ name: 'imoNumber', newValue: imoNr });
            if (appAccessToken) {
                queryParamValues.push({ name: 'appAccessToken', newValue: appAccessToken });
            }

            if (portCallId) {
                queryParamValues.push({ name: 'portCallId', newValue: portCallId.toString() });
            }

            if (reportLocation) {
                queryParamValues.push({ name: 'reportLocation', newValue: reportLocation.toString() });
            }

            if (berthCallId) {
                queryParamValues.push({ name: 'berthCallId', newValue: berthCallId.toString() });
            }

            if (cargoOperationId) {
                queryParamValues.push({ name: 'cargoOperationId', newValue: cargoOperationId.toString() });
            }

            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
        }
    };

    const hasAuthorizedAppToken = () => {
        const windowParams = new URLSearchParams(window.location.search);
        const imoNumberStr = windowParams.get('imoNumber');
        const appAccessTokenStr = windowParams.get('appAccessToken');

        return imoNumberStr && appAccessTokenStr;
    };
    const getAppAccessToken = (): string => {
        const windowParams = new URLSearchParams(window.location.search);
        return windowParams.get('appAccessToken');
    };

    return {
        getQueryParam,
        resetQueryParamPortCall,
        setQueryParamCargoTempLog,
        setQueryParamPortCall,
        setQueryParamBerthCall,
        setQueryParamCargoOperation,
        setQueryParamEvent,
        setQueryParamChemicalReportType,
        setQueryParamChemicalOrder,
        setQueryParamChemicalPortCall,
        createPortCallListQueryParam,
        createVoyageNrListQueryParam,
        setQueryFromSelectedNode,
        setQueryParamToolbox,
        hasAuthorizedAppToken,
        setAppAccessToken,
        getAppAccessToken,
    };
};
